import { altitudeToPressure } from './altitude.js';

/**
 * Render altitude (meters) labels on the left edge of the chart.
 *
 * Skysight Y-axis (decoded from live DOM 2026-05-26):
 *   - Standard ticks at 1000m steps below 6000m, 2000m steps above 6000m up
 *     to 12000m. Surface altitude added as the first tick.
 *     Style: font-size 12px, default (black) fill, x=-9, text-anchor end.
 *   - Special key-level labels (PBL height, LCL altitude, EL altitude):
 *     same position (x=-9, text-anchor end), font-size 10px,
 *     fill rgba(100, 100, 100, 0.7).
 *
 * @param {import('d3-selection').Selection} container
 * @param {import('../skewt/scales.js').Scales} scales
 * @param {Array} data
 * @param {{pblh?: number, lclAltitude?: number, elAltitude?: number}} [special]
 */
export function drawAltitudeAxis(container, scales, data, special = {}) {
    container.selectAll('.skewt-ext-altitude-axis').remove();
    if (!data || data.length === 0) return;

    const layer = container.append('g').attr('class', 'skewt-ext-altitude-axis');

    // Standard altitude ticks
    const minHght = Math.ceil(data[0].hght / 1000) * 1000;
    const maxHght = Math.floor(data[data.length - 1].hght / 1000) * 1000;
    const ticks = [];
    // Surface altitude as first tick
    ticks.push({ alt: Math.round(data[0].hght), isSurface: true });
    // 1000-6000m: every 1000m
    for (let alt = minHght; alt <= Math.min(maxHght, 6000); alt += 1000) {
        if (alt > data[0].hght) ticks.push({ alt });
    }
    // 8000m+: every 2000m up to 12000m
    for (let alt = 8000; alt <= Math.min(maxHght, 12000); alt += 2000) {
        ticks.push({ alt });
    }

    for (const tick of ticks) {
        const p = altitudeToPressure(tick.alt, data);
        if (!Number.isFinite(p)) continue;
        const y = scales.y(p);
        // Tick line
        layer
            .append('line')
            .attr('x1', -4)
            .attr('x2', 0)
            .attr('y1', y)
            .attr('y2', y)
            .attr('stroke', '#666')
            .attr('stroke-width', 0.75);
        // Standard label (12px, default black)
        layer
            .append('text')
            .attr('x', -9)
            .attr('y', y)
            .attr('dy', '0.32em')
            .attr('text-anchor', 'end')
            .style('font-size', '12px')
            .style('fill', tick.isSurface ? 'rgba(100, 100, 100, 0.7)' : '#333')
            .text(`${tick.alt}m`);
    }

    // Special key-level altitude labels (PBL / LCL / EL) — Skysight style:
    // smaller font, semi-transparent gray, no tick line (they share the line
    // already drawn by the pillbox annotation).
    const specialLabels = [
        { alt: special.pblh, cls: 'pbl-label' },
        { alt: special.lclAltitude, cls: 'lcl-altitude-label' },
        { alt: special.elAltitude, cls: 'el-altitude-label' },
    ];
    for (const sp of specialLabels) {
        if (!Number.isFinite(sp.alt)) continue;
        const p = altitudeToPressure(sp.alt, data);
        if (!Number.isFinite(p)) continue;
        const y = scales.y(p);
        layer
            .append('text')
            .attr('class', sp.cls)
            .attr('x', -9)
            .attr('y', y)
            .attr('dy', '0.32em')
            .attr('text-anchor', 'end')
            .style('font-size', '10px')
            .style('fill', 'rgba(100, 100, 100, 0.7)')
            .text(`${Math.round(sp.alt)}m`);
    }
}
